import React, { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

const ColorPicker = ({ color, onChange, onClose, onColorChange }) => {
  const [currentColor, setCurrentColor] = useState(color);
  const [rgb, setRgb] = useState({ r: 0, g: 0, b: 0 });
  const [hex, setHex] = useState(color);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const spectrumRef = useRef(null);
  const pickerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  // Handle clicks outside the picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Convert hex to rgb
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  // Convert rgb to hex
  const rgbToHex = (r, g, b) => {
    return '#' + [r, g, b].map(x => {
      const hex = x.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    }).join('');
  };

  // Calculate position from RGB values
  const calculatePositionFromRgb = (r, g, b) => {
    if (!spectrumRef.current) return { x: 0, y: 0 };
    
    const rect = spectrumRef.current.getBoundingClientRect();
    
    // Convert RGB to HSV
    const max = Math.max(r, g, b) / 255;
    const min = Math.min(r, g, b) / 255;
    const v = max;
    const s = max === 0 ? 0 : (max - min) / max;
    
    let h;
    if (max === min) {
      h = 0;
    } else if (max === r / 255) {
      h = 60 * ((g / 255 - b / 255) / (max - min));
    } else if (max === g / 255) {
      h = 60 * (2 + (b / 255 - r / 255) / (max - min));
    } else {
      h = 60 * (4 + (r / 255 - g / 255) / (max - min));
    }
    
    if (h < 0) h += 360;
    h = h / 360; // Normalize to 0-1
    
    return {
      x: h * rect.width,
      y: (1 - v) * rect.height
    };
  };

  // Initialize RGB values and position from hex
  useEffect(() => {
    const rgbValues = hexToRgb(color);
    if (rgbValues) {
      setRgb(rgbValues);
      setPosition(calculatePositionFromRgb(rgbValues.r, rgbValues.g, rgbValues.b));
    }
  }, [color]);

  // Handle spectrum click/drag
  const handleSpectrumInteraction = (e) => {
    if (!spectrumRef.current) return;
    
    const rect = spectrumRef.current.getBoundingClientRect();
    const x = Math.min(Math.max(0, e.clientX - rect.left), rect.width);
    const y = Math.min(Math.max(0, e.clientY - rect.top), rect.height);
    
    // Update position for the selector circle
    setPosition({ x, y });
    
    // Calculate color based on position
    const h = (x / rect.width);
    const s = 1;
    const v = 1 - (y / rect.height);
    
    // Convert HSV to RGB
    let r, g, b;
    
    const i = Math.floor(h * 6);
    const f = h * 6 - i;
    const p = v * (1 - s);
    const q = v * (1 - f * s);
    const t = v * (1 - (1 - f) * s);
    
    switch (i % 6) {
      case 0: r = v; g = t; b = p; break;
      case 1: r = q; g = v; b = p; break;
      case 2: r = p; g = v; b = t; break;
      case 3: r = p; g = q; b = v; break;
      case 4: r = t; g = p; b = v; break;
      case 5: r = v; g = p; b = q; break;
      default: r = 0; g = 0; b = 0;
    }
    
    const newRgb = {
      r: Math.round(r * 255),
      g: Math.round(g * 255),
      b: Math.round(b * 255)
    };
    
    setRgb(newRgb);
    const newHex = rgbToHex(newRgb.r, newRgb.g, newRgb.b);
    setHex(newHex);
    setCurrentColor(newHex);
    setPosition(calculatePositionFromRgb(newRgb.r, newRgb.g, newRgb.b));
    onColorChange?.(newHex); // Real-time update
  };

  // Handle RGB slider changes
  const handleRgbChange = (channel, value) => {
    const newRgb = { ...rgb, [channel]: parseInt(value) };
    setRgb(newRgb);
    const newHex = rgbToHex(newRgb.r, newRgb.g, newRgb.b);
    setHex(newHex);
    setCurrentColor(newHex);
    setPosition(calculatePositionFromRgb(newRgb.r, newRgb.g, newRgb.b));
    onColorChange?.(newHex); // Real-time update
  };

  // Handle hex input change
  const handleHexChange = (value) => {
    // Remove # if present for processing
    let hexValue = value.replace('#', '');
    
    // Allow typing and update display value
    setHex(value);
    
    // Process 3 or 6 character hex values
    const hex3Regex = /^[0-9A-Fa-f]{3}$/;
    const hex6Regex = /^[0-9A-Fa-f]{6}$/;
    
    let fullHex = null;
    
    if (hex3Regex.test(hexValue)) {
      // Convert 3-char hex to 6-char (e.g., '123' -> '112233')
      fullHex = `#${hexValue[0]}${hexValue[0]}${hexValue[1]}${hexValue[1]}${hexValue[2]}${hexValue[2]}`;
    } else if (hex6Regex.test(hexValue)) {
      fullHex = `#${hexValue}`;
    }
    
    if (fullHex) {
      const newRgb = hexToRgb(fullHex);
      if (newRgb) {
        setRgb(newRgb);
        setCurrentColor(fullHex);
        setPosition(calculatePositionFromRgb(newRgb.r, newRgb.g, newRgb.b));
        onColorChange?.(fullHex);
      }
    }
  };

  // Handle save
  const handleSave = () => {
    onChange(currentColor);
    onClose();
  };

  return (
    <div 
      ref={pickerRef}
      className="absolute z-50 top-[calc(100%+10px)] -left-[250px]"
      style={{
        maxHeight: 'calc(100vh - 400px)', // Ensure it doesn't overflow viewport
        overflowY: 'auto'
      }}
    >
      <div 
        className={cn(
          "p-4 rounded-xl border shadow-xl w-[300px]",
          "bg-gray-900/90 border-gray-700/50 backdrop-blur-[32px]"
        )}
      >
        {/* Color Spectrum */}
        <div 
          ref={spectrumRef}
          className="w-full h-[150px] rounded-lg mb-4 cursor-crosshair relative [transition:none]"
          style={{
            background: 'linear-gradient(to bottom, transparent, black), linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)'
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            setIsDragging(true);
            handleSpectrumInteraction(e);
          }}
          onMouseMove={(e) => {
            e.stopPropagation();
            if (isDragging) {
              handleSpectrumInteraction(e);
            }
          }}
          onMouseUp={(e) => {
            e.stopPropagation();
            setIsDragging(false);
          }}
          onMouseLeave={(e) => {
            e.stopPropagation();
            setIsDragging(false);
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Color Selection Circle */}
          <div
            className="w-4 h-4 border-2 border-white rounded-full absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none [transition:none]"
            style={{
              left: position.x,
              top: position.y,
              boxShadow: '0 0 0 1px rgba(0,0,0,0.5)'
            }}
          />
        </div>

        {/* Current Color Preview */}
        <div className="flex items-center gap-2 mb-4" onClick={(e) => e.stopPropagation()}>
          <div 
            className="w-12 h-12 rounded-lg shadow-inner [transition:none]"
            style={{ backgroundColor: currentColor }}
          />
          <input
            type="text"
            value={hex}
            onChange={(e) => handleHexChange(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            className={cn(
              "flex-1 px-2 py-1 rounded text-sm",
              "bg-gray-800/50 border-gray-700/50 border",
              "text-gray-100 placeholder-gray-500",
              "focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/30",
              "[transition:none]"
            )}
            placeholder="Enter hex..."
            maxLength={7}
          />
        </div>

        {/* RGB Sliders */}
        <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
          {/* Red */}
          <div>
            <div className="flex justify-between text-xs text-gray-400 mb-1">
              <span>R</span>
              <span>{rgb.r}</span>
            </div>
            <input
              type="range"
              min="0"
              max="255"
              value={rgb.r}
              onChange={(e) => handleRgbChange('r', e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="w-full h-2 bg-gradient-to-r from-black to-red-500 rounded-lg appearance-none cursor-pointer"
            />
          </div>
          {/* Green */}
          <div>
            <div className="flex justify-between text-xs text-gray-400 mb-1">
              <span>G</span>
              <span>{rgb.g}</span>
            </div>
            <input
              type="range"
              min="0"
              max="255"
              value={rgb.g}
              onChange={(e) => handleRgbChange('g', e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="w-full h-2 bg-gradient-to-r from-black to-green-500 rounded-lg appearance-none cursor-pointer"
            />
          </div>
          {/* Blue */}
          <div>
            <div className="flex justify-between text-xs text-gray-400 mb-1">
              <span>B</span>
              <span>{rgb.b}</span>
            </div>
            <input
              type="range"
              min="0"
              max="255"
              value={rgb.b}
              onChange={(e) => handleRgbChange('b', e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="w-full h-2 bg-gradient-to-r from-black to-blue-500 rounded-lg appearance-none cursor-pointer"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 mt-4" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="px-3 py-1 text-sm text-gray-300 hover:text-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleSave();
            }}
            className="px-3 py-1 text-sm bg-purple-500 hover:bg-purple-600 text-white rounded transition-colors"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default ColorPicker; 